import { ACTIVE_CLASS } from '../shared/constants.js';
import { prefersReducedMotion, readPxValue } from '../shared/browser.js';

const getBannerParts = (track) => ({
  counter: track.querySelector('.banner__slider-counter'),
  counts: Array.from(track.querySelectorAll('.banner__slider-count')),
  dynamic: track.querySelector('.banner__slider--dynamic'),
  slides: Array.from(track.querySelectorAll('.banner__slider-slide')),
});

const getBannerIndex = (progress, total) =>
  Math.min(total - 1, Math.floor(progress * total));

const isMobileBannerLayout = () => window.matchMedia('(max-width: 767px)').matches;

const getSegmentProgress = (index, total) => (index + 0.5) / total;

const scrollToSegment = (scrollTrigger, index, total) => {
  const start = scrollTrigger.start || 0;
  const end = scrollTrigger.end || start;
  const top = start + (end - start) * getSegmentProgress(index, total);

  window.scrollTo({
    top,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
};

const measureBanner = (state) => {
  const tallestSlide = state.slides.reduce((height, slide) => {
    const content = slide.querySelector('.banner__slider-bot');

    return Math.max(height, content ? content.offsetHeight : slide.offsetHeight);
  }, 0);

  if (tallestSlide > 0) {
    state.dynamic.style.minHeight = `${tallestSlide}px`;
  }

  state.countStep = readPxValue(state.track, '--banner-counter-gap', 0);
};

const syncBannerCounts = (state, nextIndex, immediate = false) => {
  state.counts.forEach((count, index) => {
    const x = (index - nextIndex) * state.countStep;

    count.classList.toggle(ACTIVE_CLASS, index === nextIndex);
    count.setAttribute('aria-current', index === nextIndex ? 'true' : 'false');
    count.setAttribute('aria-disabled', index === nextIndex ? 'true' : 'false');

    if (immediate) {
      state.gsap.set(count, { x });
      return;
    }

    state.gsap.to(count, {
      duration: 0.42,
      ease: 'power3.out',
      overwrite: 'auto',
      x,
    });
  });
};

const syncBannerSlides = (state, nextIndex, immediate = false) => {
  const currentSlide = state.slides[state.activeIndex];
  const nextSlide = state.slides[nextIndex];

  state.slides.forEach((slide, index) => {
    slide.classList.toggle(ACTIVE_CLASS, index === nextIndex);
    slide.setAttribute('aria-hidden', String(index !== nextIndex));
  });

  if (immediate || state.useInstantSlides || currentSlide === nextSlide) {
    state.slideTimeline?.kill();
    state.gsap.set(state.slides, { autoAlpha: 0 });
    state.gsap.set(nextSlide, { autoAlpha: 1 });
    return;
  }

  state.slideTimeline?.kill();
  state.gsap.set(state.slides.filter((slide) => slide !== currentSlide), {
    autoAlpha: 0,
  });
  state.slideTimeline = state.gsap.timeline({
    defaults: { overwrite: 'auto' },
  });

  state.slideTimeline
    .to(currentSlide, {
      autoAlpha: 0,
      duration: 0.18,
      ease: 'power1.out',
    })
    .fromTo(
      nextSlide,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: 0.28,
        ease: 'power1.inOut',
      },
      0.16
    );
};

const setBannerIndex = (state, nextIndex, immediate = false) => {
  if (!immediate && nextIndex === state.activeIndex) {
    return;
  }

  syncBannerCounts(state, nextIndex, immediate);
  syncBannerSlides(state, nextIndex, immediate);
  state.activeIndex = nextIndex;
};

const initBannerCountControls = (state, scrollTrigger) => {
  state.counts.forEach((count, index) => {
    if (!['A', 'BUTTON'].includes(count.tagName)) {
      count.setAttribute('role', 'button');
      count.setAttribute('tabindex', '0');
    }

    count.addEventListener('click', (event) => {
      event.preventDefault();

      if (state.activeIndex === index) {
        return;
      }

      scrollToSegment(scrollTrigger, index, state.slides.length);
    });

    count.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();

      if (state.activeIndex !== index) {
        scrollToSegment(scrollTrigger, index, state.slides.length);
      }
    });
  });
};

export const initBannerSlider = (track, gsap, ScrollTrigger) => {
  if (
    track.dataset.bannerReady === 'true' ||
    !gsap ||
    !ScrollTrigger ||
    prefersReducedMotion()
  ) {
    return;
  }

  const parts = getBannerParts(track);

  if (
    !parts.counter ||
    !parts.dynamic ||
    !parts.counts.length ||
    parts.counts.length !== parts.slides.length
  ) {
    return;
  }

  track.dataset.bannerReady = 'true';

  const state = {
    activeIndex: 0,
    countStep: 0,
    counts: parts.counts,
    dynamic: parts.dynamic,
    gsap,
    slideTimeline: null,
    slides: parts.slides,
    track,
    useInstantSlides: isMobileBannerLayout(),
  };

  measureBanner(state);
  setBannerIndex(state, 0, true);

  const scrollTrigger = ScrollTrigger.create({
    trigger: track,
    start: 'top top',
    end: 'bottom bottom',
    invalidateOnRefresh: true,
    onRefresh: (self) => {
      measureBanner(state);
      setBannerIndex(state, getBannerIndex(self.progress, state.slides.length), true);
    },
    onUpdate: (self) => {
      setBannerIndex(state, getBannerIndex(self.progress, state.slides.length));
    },
  });

  initBannerCountControls(state, scrollTrigger);
};
